/**
 * Animation system for Turn Connect applications.
 * Consistent transitions, timing, and easing across platforms.
 */

export const animations = {
  // Durations (in milliseconds)
  duration: {
    instant: 0,
    fast: 150, // Quick micro-interactions
    normal: 250, // Standard transitions
    slow: 350, // Complex transitions
    slower: 500, // Page transitions
    slowest: 750, // Heavy animations
  },

  // Easing functions
  easing: {
    // Standard easing
    ease: 'cubic-bezier(0.25, 0.1, 0.25, 1)',
    easeIn: 'cubic-bezier(0.42, 0, 1, 1)',
    easeOut: 'cubic-bezier(0, 0, 0.58, 1)',
    easeInOut: 'cubic-bezier(0.42, 0, 0.58, 1)',

    // Spring-like easing
    spring: 'cubic-bezier(0.68, -0.55, 0.265, 1.55)',

    // iOS-like easing
    iosAccelerate: 'cubic-bezier(0.4, 0, 1, 1)',
    iosDecelerate: 'cubic-bezier(0, 0, 0.2, 1)',
    iosStandard: 'cubic-bezier(0.4, 0, 0.2, 1)',

    // Material Design easing
    materialAccelerate: 'cubic-bezier(0.4, 0, 1, 1)',
    materialDecelerate: 'cubic-bezier(0, 0, 0.2, 1)',
    materialStandard: 'cubic-bezier(0.4, 0, 0.2, 1)',
    materialEmphasized: 'cubic-bezier(0.2, 0, 0, 1)',
  },

  // Pre-defined transitions
  transitions: {
    // Opacity
    fadeIn: {
      property: 'opacity',
      duration: 250,
      easing: 'cubic-bezier(0, 0, 0.2, 1)',
      from: 0,
      to: 1,
    },
    fadeOut: {
      property: 'opacity',
      duration: 200,
      easing: 'cubic-bezier(0.4, 0, 1, 1)',
      from: 1,
      to: 0,
    },

    // Scale
    scaleIn: {
      property: 'transform',
      duration: 250,
      easing: 'cubic-bezier(0, 0, 0.2, 1)',
      from: 'scale(0.95)',
      to: 'scale(1)',
    },
    scaleOut: {
      property: 'transform',
      duration: 200,
      easing: 'cubic-bezier(0.4, 0, 1, 1)',
      from: 'scale(1)',
      to: 'scale(0.95)',
    },

    // Slide
    slideUp: {
      property: 'transform',
      duration: 300,
      easing: 'cubic-bezier(0, 0, 0.2, 1)',
      from: 'translateY(20px)',
      to: 'translateY(0)',
    },
    slideDown: {
      property: 'transform',
      duration: 250,
      easing: 'cubic-bezier(0.4, 0, 1, 1)',
      from: 'translateY(0)',
      to: 'translateY(20px)',
    },
    slideLeft: {
      property: 'transform',
      duration: 300,
      easing: 'cubic-bezier(0, 0, 0.2, 1)',
      from: 'translateX(20px)',
      to: 'translateX(0)',
    },
    slideRight: {
      property: 'transform',
      duration: 300,
      easing: 'cubic-bezier(0, 0, 0.2, 1)',
      from: 'translateX(-20px)',
      to: 'translateX(0)',
    },

    // Button interactions
    buttonPress: {
      property: 'transform',
      duration: 150,
      easing: 'cubic-bezier(0.4, 0, 0.2, 1)',
      from: 'scale(1)',
      to: 'scale(0.98)',
    },
    buttonRelease: {
      property: 'transform',
      duration: 150,
      easing: 'cubic-bezier(0, 0, 0.2, 1)',
      from: 'scale(0.98)',
      to: 'scale(1)',
    },

    // Loading states
    pulse: {
      property: 'opacity',
      duration: 1500,
      easing: 'ease-in-out',
      infinite: true,
      alternate: true,
      from: 0.4,
      to: 1,
    },
    spin: {
      property: 'transform',
      duration: 1000,
      easing: 'linear',
      infinite: true,
      from: 'rotate(0deg)',
      to: 'rotate(360deg)',
    },
  },

  // CSS helpers for common animations
  css: {
    // Fade animations
    fadeIn: `
      animation: fadeIn 250ms cubic-bezier(0, 0, 0.2, 1) forwards;
      @keyframes fadeIn {
        from { opacity: 0; }
        to { opacity: 1; }
      }
    `,
    fadeOut: `
      animation: fadeOut 200ms cubic-bezier(0.4, 0, 1, 1) forwards;
      @keyframes fadeOut {
        from { opacity: 1; }
        to { opacity: 0; }
      }
    `,

    // Scale animations
    scaleIn: `
      animation: scaleIn 250ms cubic-bezier(0, 0, 0.2, 1) forwards;
      @keyframes scaleIn {
        from { transform: scale(0.95); opacity: 0; }
        to { transform: scale(1); opacity: 1; }
      }
    `,
    scaleOut: `
      animation: scaleOut 200ms cubic-bezier(0.4, 0, 1, 1) forwards;
      @keyframes scaleOut {
        from { transform: scale(1); opacity: 1; }
        to { transform: scale(0.95); opacity: 0; }
      }
    `,

    // Loading animations
    pulse: `
      animation: pulse 1.5s ease-in-out infinite alternate;
      @keyframes pulse {
        from { opacity: 0.4; }
        to { opacity: 1; }
      }
    `,
    spin: `
      animation: spin 1s linear infinite;
      @keyframes spin {
        from { transform: rotate(0deg); }
        to { transform: rotate(360deg); }
      }
    `,

    // Slide animations
    slideUp: `
      animation: slideUp 300ms cubic-bezier(0, 0, 0.2, 1) forwards;
      @keyframes slideUp {
        from { transform: translateY(20px); opacity: 0; }
        to { transform: translateY(0); opacity: 1; }
      }
    `,
    slideDown: `
      animation: slideDown 250ms cubic-bezier(0.4, 0, 1, 1) forwards;
      @keyframes slideDown {
        from { transform: translateY(0); opacity: 1; }
        to { transform: translateY(20px); opacity: 0; }
      }
    `,
  },
};

export type Animations = typeof animations;
